function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import { api } from '@rd-web-markets/shared/dist/services/service';
import { useCallback, useEffect, useState } from 'react';
import claimSubcontractorsService from './claim_subcontractors.service';
import claimExternalWorkersService from './claim_external_workers.service';
import claimConsumablesService from './claim_consumables.service';
import cloudComputingsService from './cloud_computings.service';
import dataLicencesCostsService from './data_licences_costs.service';
import claimSoftwareConsumablesService from '../claim_software_consumables.service';
import claimEmployeesService from './claim_employees.service';
import independentRndContributionsService from './independent_rnd_contributions.service';
import paymentsToClinicalTrialsService from './payments_to_clinical_trials.service';

// only the services that are actually used in the tables
const servicesPerCostFragmentType = {
  'UkSection::ClaimSubcontractor': claimSubcontractorsService,
  'UkSection::ClaimExternalWorker': claimExternalWorkersService,
  'UkSection::ClaimConsumable': claimConsumablesService,
  'UkSection::CloudComputing': cloudComputingsService,
  'UkSection::DataLicencesCost': dataLicencesCostsService,
  'UkSection::IndependentRndContribution': independentRndContributionsService,
  'UkSection::ClaimSoftwareConsumable': claimSoftwareConsumablesService,
  'UkSection::ClaimEmployee': claimEmployeesService,
  'UkSection::PaymentsToClinicalTrial': paymentsToClinicalTrialsService
};
const costFragmentsService = {
  useFetchChildCostFragments: (claimId, costFragmentType) => {
    const fetchAll = useErrorHandling(useCallback(async commercialProjectId => {
      const response = await costFragmentsService.all(claimId, costFragmentType, commercialProjectId);
      return response.cost_fragments;
    }, [claimId, costFragmentType]));
    return fetchAll;
  },
  useGetAll: (claimId, costFragmentType) => {
    const [costFragments, setCostFragments] = useState(null);
    const [totals, setTotals] = useState(null);
    const fetchAll = useErrorHandling(useCallback(async (claimId, costFragmentType) => {
      const response = await costFragmentsService.all(claimId, costFragmentType);
      setCostFragments(response.cost_fragments);
      setTotals(response.totals);
    }, []));
    useEffect(() => {
      if (claimId) {
        fetchAll(claimId, costFragmentType);
      }
    }, [claimId, costFragmentType, fetchAll]);
    return [costFragments, setCostFragments, fetchAll, totals, setTotals];
  },
  useUpdate: (costFragments, setCostFragments, setTotals) => {
    return useErrorHandling(useCallback(async costFragmentId => {
      const index = costFragments.findIndex(cf => cf.id === costFragmentId);
      const result = await costFragmentsService.update(costFragments[index]);
      const updatedCostFragment = result.cost_fragment;
      setTotals(result.totals);
      setCostFragments(costFragments => {
        const index = costFragments.findIndex(cf => cf.id === costFragmentId);
        costFragments[index] = _objectSpread(_objectSpread({}, costFragments[index]), updatedCostFragment);
        return [...costFragments];
      });
      return updatedCostFragment;
    }, [costFragments, setCostFragments, setTotals]));
  },
  useUpdateFragmentable: (costFragments, setCostFragments, setTotals) => {
    return useErrorHandling(useCallback(async costFragmentId => {
      const costFragment = costFragments.find(cf => cf.id === costFragmentId);
      const updateData = _objectSpread({
        id: costFragment.fragmentable_id,
        claim_id: costFragment.claim_id
      }, costFragment.fragmentable);
      await servicesPerCostFragmentType[costFragment.fragmentable_type].update(updateData);
      const updateCostFragmentResult = await costFragmentsService.get(costFragment);
      const updatedCostFragment = updateCostFragmentResult.cost_fragment;
      const totals = updateCostFragmentResult.totals;
      setTotals(totals);
      setCostFragments(costFragments => {
        const index = costFragments.findIndex(cf => cf.id === costFragmentId);

        // We only update the changed fields because rows can carry table-only UI state
        // such as expansion metadata that should survive a backend refresh.
        costFragments[index] = _objectSpread(_objectSpread({}, costFragments[index]), updatedCostFragment);
        return [...costFragments];
      });
    }, [costFragments, setCostFragments, setTotals]));
  },
  useCreate: (costFragments, setCostFragments, setTotals) => {
    return useErrorHandling(useCallback(async costFragmentId => {
      const index = costFragments.findIndex(cf => cf.id === costFragmentId);
      const costFragment = _objectSpread({}, costFragments[index]);
      const fragmentable = _objectSpread(_objectSpread({}, costFragment.fragmentable), {}, {
        claim_id: costFragment.claim_id
      });
      delete costFragment.fragmentable;
      const result = await costFragmentsService.create({
        claim_id: costFragment.claim_id,
        cost_fragment: costFragment,
        fragmentable: fragmentable
      });
      const createdCostFragment = result.cost_fragment;
      const totals = result.totals;
      costFragments[index] = createdCostFragment;
      setTotals(totals);
      setCostFragments([...costFragments]);
      return createdCostFragment;
    }, [costFragments, setCostFragments, setTotals]));
  },
  useDelete: onDelete => {
    return useErrorHandling(useCallback(async costFragment => {
      const response = await costFragmentsService.delete(costFragment);
      onDelete(costFragment.id, response.totals);
    }, [onDelete]));
  },
  get: cost_fragment => api.get("/claims/".concat(cost_fragment.claim_id, "/cost_fragments/").concat(cost_fragment.id)),
  update: cost_fragment => api.update("/claims/".concat(cost_fragment.claim_id, "/cost_fragments/").concat(cost_fragment.id), {
    cost_fragment
  }),
  all: function (claimId, costFragmentType) {
    let commercialProjectId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
    return api.get("/claims/".concat(claimId, "/cost_fragments?fragmentable_type=").concat(costFragmentType).concat(commercialProjectId ? "&commercial_project_id=".concat(commercialProjectId) : ''));
  },
  delete: cost_fragment => api.delete("/claims/".concat(cost_fragment.claim_id, "/cost_fragments/").concat(cost_fragment.id)),
  create: cost_fragment => api.create("/claims/".concat(cost_fragment.claim_id, "/cost_fragments"), cost_fragment)
};
export default costFragmentsService;